import { INotificationAppDisplay } from '../interfaces';

/** Used when an application has no Color set — Core.BusinessApplication.Color is nullable. */
const FALLBACK_COLOR = '#5f6368';
const TINT_ALPHA = 0.14;

/**
 * Accepts anything carrying the application's identity and color — both IBusinessApplication
 * (settings dialog) and IUserNotification (menu rows) satisfy this shape.
 */
export function buildAppDisplay(application: { businessApplicationID: number; applicationName: string; color: string }): INotificationAppDisplay {
	const color = normalizeColor(application.color) ?? FALLBACK_COLOR;

	return {
		businessApplicationID: application.businessApplicationID,
		appName: application.applicationName,
		color,
		tintColor: toTint(color),
		tintTextColor: color
	};
}

/** Color is a nullable varchar(30), so it may arrive with or without the leading '#'. */
function normalizeColor(raw: string): string {
	const value = raw?.trim();

	if (!value) {
		return null;
	}

	return /^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(value) ? `#${value}` : value;
}

function toTint(color: string): string {
	const hex = color.replace('#', '');
	const expanded = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;

	if (!/^[0-9a-f]{6}$/i.test(expanded)) {
		// named colors and rgb()/hsl() strings can't be mixed here; fall back to a neutral tint
		return 'rgba(0, 0, 0, 0.08)';
	}

	const r = parseInt(expanded.slice(0, 2), 16);
	const g = parseInt(expanded.slice(2, 4), 16);
	const b = parseInt(expanded.slice(4, 6), 16);

	return `rgba(${r}, ${g}, ${b}, ${TINT_ALPHA})`;
}
